import { Box, Checkbox, Flex, Text, useToken } from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { useRef, useState } from 'react';

import type { PostingDiscoveryRow } from '@revelio/data-access';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import type { TableRow } from '../../TableSelectionProvider/TableSelectionProvider';
import { FullPostingInfo } from '../FullPostingInfo/FullPostingInfo';
import {
  formatSalaryRange,
  relativePostedAt,
} from '../FullPostingInfo/postingFormat';
import { transformRowToPostingData } from '../FullPostingInfo/transformRowToPostingData';
import pdStyles from './PostingDiscoveryCardView.module.css';
import { CardPopout } from './RowPopoutSelector';
import styles from './TableChart.module.css';

const SELECTED_BG = 'green.50';
const CARD_HEIGHT = 100;

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const toStringValue = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

interface PostingDiscoveryCardViewProps {
  data: PostingDiscoveryRow[];
  enableRowSelection?: boolean;
  onRowSelectionChange?: (rows: TableRow[]) => void;
  selectedRows?: PostingDiscoveryRow[];
  showZebraStripe?: boolean;
}

export const PostingDiscoveryCardView = ({
  data,
  enableRowSelection,
  onRowSelectionChange,
  selectedRows = [],
  showZebraStripe = true,
}: PostingDiscoveryCardViewProps) => {
  const zebraStripeColor = useToken('colors', 'lightBlue.50');
  // Salary values arrive already converted (PostingDiscoveryDataProvider); format
  // with the shared currency formatter (rounded/abbreviated, matching TD).
  const { code: currencyCode } = useCurrencyPreference();

  const [expandedBenefitsCards, setExpandedBenefitsCards] = useState<
    Set<number>
  >(new Set());

  const toggleBenefitsExpanded = (index: number) => {
    setExpandedBenefitsCards((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const selectedPostingIds = new Set(selectedRows.map((row) => row.job_id));

  const handleCardClick = (cardData: PostingDiscoveryRow) => {
    if (!enableRowSelection || !onRowSelectionChange) return;

    const postingId = cardData.job_id;
    const isSelected = selectedPostingIds.has(postingId);

    const newSelection: PostingDiscoveryRow[] = isSelected
      ? selectedRows.filter((row) => row.job_id !== postingId)
      : [...selectedRows, cardData];

    onRowSelectionChange(newSelection);
  };

  const parentRef = useRef<HTMLDivElement>(null);

  const popoutGridRef = useRef({
    getScrollLeft: () => parentRef.current?.scrollLeft ?? 0,
    getDOMNode: () => parentRef.current,
  }).current;

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_HEIGHT,
    overscan: 8,
    measureElement: (element) =>
      element?.getBoundingClientRect().height ?? CARD_HEIGHT,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <Box
      ref={parentRef}
      w="full"
      h="full"
      overflow="auto"
      position="relative"
      className={pdStyles.scrollContainer}
    >
      <Box
        position="relative"
        width="100%"
        height={`${virtualizer.getTotalSize()}px`}
      >
        {virtualItems.map((virtualRow) => {
          const index = virtualRow.index;
          const cardData = data[index];
          if (!cardData) return null;
          const isLastCard = index === data.length - 1;

          const title = toStringValue(cardData.jobtitle);
          const company = toStringValue(cardData.company_name);
          const jobType = toStringValue(cardData.remote_type);
          const location = toStringValue(cardData.location);
          // const salaryMin = toNumber(cardData.salary_min);
          const salaryMax = toNumber(cardData.salary);
          /** TODO: Benefits not yet returned by BE */
          // const benefits = toStringArray(cardData.benefits);
          const benefits: string[] = [];
          const postedAt = toStringValue(cardData.post_date);

          const postingId = cardData.job_id;
          const isSelected = selectedPostingIds.has(postingId);

          const locationShort = (() => {
            if (!location) return '';
            if (location.toLowerCase().startsWith('hybrid')) return 'Hybrid';
            if (location.toLowerCase().startsWith('onsite')) return 'Onsite';
            return location;
          })();

          const showLocationSegment = location && location !== locationShort;
          const metaSegments = [
            showLocationSegment ? location : '',
            locationShort,
            jobType,
            relativePostedAt(postedAt),
          ].filter((segment) => segment.length > 0);

          const salaryText = formatSalaryRange(0, salaryMax, currencyCode);

          let bgColor: string = 'white';
          if (isSelected) {
            bgColor = SELECTED_BG;
          } else if (showZebraStripe && index % 2 === 0) {
            bgColor = zebraStripeColor;
          }

          const cardContent = (
            <Flex gap={3} align="start">
              {enableRowSelection && (
                <Checkbox
                  isChecked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleCardClick(cardData)}
                  colorScheme="green"
                  mt="2px"
                />
              )}

              <Box flex={1} minW={0}>
                <Text color="#35ABDD" className={pdStyles.title}>
                  {title}
                </Text>

                <Text className={pdStyles.meta}>
                  {company && (
                    <>
                      <Text as="span" fontWeight={700}>
                        {company}
                      </Text>
                      {metaSegments.length > 0 && (
                        <Text as="span">   •   </Text>
                      )}
                    </>
                  )}
                  <Text as="span">{metaSegments.join('   •   ')}</Text>
                </Text>

                {(salaryText || benefits.length > 0) && (
                  <Box className={pdStyles.salaryLine}>
                    {salaryText && (
                      <Text as="span" className={pdStyles.salaryAmount}>
                        {salaryText}
                      </Text>
                    )}
                    {(() => {
                      const isExpanded = expandedBenefitsCards.has(index);
                      const visibleCount = isExpanded
                        ? benefits.length
                        : Math.min(5, benefits.length);
                      const hiddenCount = benefits.length - visibleCount;
                      const visible = benefits.slice(0, visibleCount);

                      return (
                        <>
                          {visible.map((benefit, idx) => (
                            <Text
                              as="span"
                              key={`benefit-${idx}`}
                              className={pdStyles.benefitTag}
                            >
                              {benefit}
                            </Text>
                          ))}
                          {benefits.length > 5 && (
                            <Box
                              as="span"
                              className={pdStyles.benefitMore}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                toggleBenefitsExpanded(index);
                              }}
                            >
                              {isExpanded
                                ? 'Show Less'
                                : `+${hiddenCount} More`}
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            </Flex>
          );

          return (
            <CardPopout
              key={String(postingId ?? index)}
              boundaryRef={parentRef}
              gridRef={popoutGridRef}
              rowData={cardData as TableRow}
              content={
                <FullPostingInfo data={transformRowToPostingData(cardData)} />
              }
            >
              {({ onClick, anchorRef }) => (
                <Box
                  data-index={index}
                  ref={(node: HTMLDivElement | null) => {
                    virtualizer.measureElement(node);
                    anchorRef(node);
                  }}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  minHeight={`${CARD_HEIGHT}px`}
                  transform={`translateY(${virtualRow.start}px)`}
                  bg={bgColor}
                  className={classNames(
                    styles.cardItem,
                    isLastCard && styles.cardItemLast,
                    pdStyles.cardRow
                  )}
                  onClick={onClick}
                >
                  {cardContent}
                </Box>
              )}
            </CardPopout>
          );
        })}
      </Box>
    </Box>
  );
};
